import React from 'react';
import { Box, Skeleton, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import { formatAgo } from '../../../utils/relativeTime';
import { useNow } from '../../../utils/useNow';
import { CHART_RANGE_KEYS, type ChartRangeKey } from './chartDelta';
import { LegendChips, LegendList } from './LegendChips';
import { PHONE_OVERLAY_HEIGHT, type PhoneLegendItem } from './phoneChartOptions';

export interface PhoneChartProps {
  /** The metric and its unit, e.g. "Temperature (°C)"; no title row
   *  without it (and without newestAt). */
  title?: React.ReactNode;
  /** The newest reading on the chart (epoch ms): "Newest 4 min ago" on the
   *  right of the title row. */
  newestAt?: number | null;
  /** The time the age is measured from; a minute clock when absent. */
  now?: number;
  /** Shown between the title row and the range control (e.g. the Location
   *  tab's chart kind chips). */
  toolbar?: React.ReactNode;
  range: ChartRangeKey;
  onRangeChange: (range: ChartRangeKey) => void;
  /** false hides the range control, for a chart that can never draw
   *  anything (a weather station, a location without a reading). */
  showRange?: boolean;
  /** The chart (phoneChartOptions); memoise it, as each new object updates
   *  the chart. */
  options: Highcharts.Options | null;
  /** The legend chips, shown when there is more than one series. */
  legend?: PhoneLegendItem[];
  hidden?: ReadonlySet<string>;
  onToggleSeries?: (id: string) => void;
  onShowAllSeries?: () => void;
  /** The chart's height (the skeleton and empty state take the same). */
  height?: number;
  /** The first load: a skeleton in place of the chart. */
  loading?: boolean;
  /** Nothing to plot: the empty state in place of the chart. */
  empty?: boolean;
  /** The empty state's text; "No readings to chart yet" when absent. */
  emptyText?: React.ReactNode;
  /** Shown under the chart (e.g. the strength chart's tested-range note). */
  footer?: React.ReactNode;
  /** 'list': the legend as rows with each line's newest value (the Charts
   *  tab); 'chips' (default): wrapping chips. */
  legendStyle?: 'chips' | 'list';
}

const NOTHING_HIDDEN: ReadonlySet<string> = new Set<string>();

/**
 * One phone chart: an optional title row (metric and unit, with the newest
 * reading's age on the right), a full-width 6h / 24h / 3d / All range
 * control with 44px buttons (unless showRange is false), the Highcharts
 * Stock chart, and the legend
 * chips below it. The first load shows a skeleton of the chart's height;
 * nothing to plot shows "No readings to chart yet".
 */
export const PhoneChart: React.FC<PhoneChartProps> = ({
  title,
  newestAt,
  now,
  toolbar,
  range,
  onRangeChange,
  showRange = true,
  options,
  legend,
  hidden = NOTHING_HIDDEN,
  onToggleSeries,
  onShowAllSeries,
  height = PHONE_OVERLAY_HEIGHT,
  loading = false,
  empty = false,
  emptyText,
  footer,
  legendStyle = 'chips',
}) => {
  const { t } = useTranslation();
  const clock = useNow();
  const refNow = now ?? clock;
  const age = newestAt != null ? formatAgo(newestAt, refNow, t) : '';

  const rangeLabels: Record<ChartRangeKey, string> = {
    '6h': t('mobile.charts.range6h', { defaultValue: '6h' }),
    '24h': t('mobile.charts.range24h', { defaultValue: '24h' }),
    '3d': t('mobile.charts.range3d', { defaultValue: '3d' }),
    all: t('mobile.charts.rangeAll', { defaultValue: 'All' }),
  };

  let body: React.ReactNode;
  if (loading) {
    body = (
      <Skeleton
        variant="rectangular"
        height={height}
        data-testid="phone-chart-skeleton"
        aria-label={t('mobile.charts.loading', { defaultValue: 'Loading the chart' })}
        sx={{ borderRadius: 1 }}
      />
    );
  } else if (empty || !options) {
    body = (
      <Box
        data-testid="phone-chart-empty"
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          px: 2,
          borderRadius: 1,
          bgcolor: 'grey.50',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {emptyText ?? t('mobile.charts.noReadings', { defaultValue: 'No readings to chart yet' })}
        </Typography>
      </Box>
    );
  } else {
    body = (
      <Box sx={{ minHeight: height, minWidth: 0 }}>
        <HighchartsReact highcharts={Highcharts} constructorType="stockChart" options={options} />
      </Box>
    );
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      {(title || age) && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1, minWidth: 0 }}>
          {title && (
            <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
              {title}
            </Typography>
          )}
          {age && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ml: 'auto', flexShrink: 0, textAlign: 'right' }}
            >
              {t('mobile.charts.newest', { defaultValue: 'Newest {{ago}}', ago: age })}
            </Typography>
          )}
        </Box>
      )}
      {toolbar}
      {showRange && (
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={range}
          onChange={(_event, next: ChartRangeKey | null) => {
            if (next) onRangeChange(next);
          }}
          aria-label={t('mobile.charts.rangeLabel', { defaultValue: 'Chart range' })}
          // A segmented switch: a grey track, the picked range a white pill.
          sx={{ mb: 1, p: '3px', gap: '3px', bgcolor: '#eceef1', borderRadius: '10px' }}
        >
          {CHART_RANGE_KEYS.map((key) => (
            <ToggleButton
              key={key}
              value={key}
              sx={{
                minHeight: 40,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                color: 'text.secondary',
                border: 'none !important',
                borderRadius: '8px !important',
                '&.Mui-selected, &.Mui-selected:hover': {
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  fontWeight: 600,
                  boxShadow: '0 1px 2px rgba(0,0,0,.1)',
                },
              }}
            >
              {rangeLabels[key]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )}
      {body}
      {!loading && !empty && options && legend && legend.length > 1 && onToggleSeries && (
        legendStyle === 'list' ? (
          <LegendList items={legend} hidden={hidden} onToggle={onToggleSeries} onShowAll={onShowAllSeries ?? (() => {})} />
        ) : (
          <LegendChips
            items={legend}
            hidden={hidden}
            onToggle={onToggleSeries}
            onShowAll={onShowAllSeries ?? (() => {})}
          />
        )
      )}
      {footer}
    </Box>
  );
};

export default PhoneChart;
